import { useState } from 'react';
import type { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import { ProcessingNotice } from '@/components/ui/processing-notice';
import {
  downloadMarketplaceReport,
  MarketplaceEndpoint,
  MarketplaceInputs,
  MarketplaceOptions,
  MarketplaceSummary,
  reconcileMarketplace,
} from '../../api/reconToolsApi';
import { downloadBlob } from '../../api/files';
import { Field, inputClass, RecordTable, Section, Stat } from './reconUi';

export interface MarketplaceConfig {
  endpoint: MarketplaceEndpoint;
  platforms: { value: string; label: string }[];
  erpLabel: string;
  platformFileLabel: string;
  defaultCycleDays: number;
  /** Quick commerce only: the stock-holding limit field. */
  stockHoldingDays?: boolean;
  /** Optional extra uploads, keyed by the backend form field. */
  optionalFiles: { field: string; label: string; note: string }[];
  steps: string[];
}

interface PanelProps {
  mission: Mission;
  onBack: () => void;
  config: MarketplaceConfig;
}

const HEADLINE_LABELS: Record<string, string> = {
  payout_matched: 'Payout matched',
  payout_mismatch: 'Payout mismatch',
  cancelled: 'Cancelled / RTO',
  erp_only: 'ERP only',
  platform_only: 'Platform only',
  stock_matched: 'Stock matched',
  stock_mismatch: 'Stock mismatch',
  stock_erp_only: 'Stock: ERP only',
  stock_platform_only: 'Stock: platform only',
};
const BAD_HEADLINES = new Set(['payout_mismatch', 'erp_only', 'platform_only', 'stock_mismatch', 'stock_erp_only', 'stock_platform_only']);

function MarketplaceReconPanel({ mission, onBack, config }: PanelProps) {
  const [platform, setPlatform] = useState(config.platforms[0].value);
  const [erp, setErp] = useState<File[]>([]);
  const [platformFile, setPlatformFile] = useState<File[]>([]);
  const [optional, setOptional] = useState<Record<string, File[]>>({});
  const [matchOn, setMatchOn] = useState('payout');
  const [tolerance, setTolerance] = useState('1');
  const [cycleDays, setCycleDays] = useState(String(config.defaultCycleDays));
  const [holdingDays, setHoldingDays] = useState('15');
  const [asOf, setAsOf] = useState('');
  const [stripZeros, setStripZeros] = useState(false);

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<MarketplaceSummary | null>(null);
  const [downloading, setDownloading] = useState(false);

  const ready = erp.length > 0 && platformFile.length > 0;

  const inputs = (): MarketplaceInputs => ({
    erp: erp[0],
    platform: platformFile[0],
    optional: Object.fromEntries(config.optionalFiles.map((f) => [f.field, optional[f.field]?.[0]])),
  });
  const options = (): MarketplaceOptions => ({
    platform,
    match_on: matchOn,
    tolerance: Number(tolerance) || 0,
    settlement_cycle_days: Number(cycleDays) || config.defaultCycleDays,
    stock_holding_days: Number(holdingDays) || 15,
    strip_leading_zeros: stripZeros,
    as_of: asOf,
  });

  const fail = (message: string) => {
    setError(message);
    setStatus('error');
  };

  const handleRun = async () => {
    if (!ready) {
      fail(`Upload the ${config.erpLabel} and the ${config.platformFileLabel}.`);
      return;
    }
    setStatus('loading');
    setError(null);
    setSummary(null);
    try {
      setSummary(await reconcileMarketplace(config.endpoint, inputs(), options()));
      setStatus('success');
    } catch (err) {
      fail(err instanceof Error ? err.message : 'Reconciliation failed.');
    }
  };

  const handleDownload = async () => {
    if (!ready) return;
    setDownloading(true);
    try {
      const { blob, filename } = await downloadMarketplaceReport(config.endpoint, inputs(), options());
      downloadBlob(blob, filename);
    } catch (err) {
      fail(err instanceof Error ? err.message : 'Failed to build the report.');
    } finally {
      setDownloading(false);
    }
  };

  const fileProps = { accept: mission.accept, hint: mission.maxSizeLabel, accentColor: mission.color };
  const platformLabel = config.platforms.find((p) => p.value === platform)?.label ?? platform;

  return (
    <MissionShell mission={mission} onBack={onBack} steps={config.steps} wide backLabel="Reconciliation Tools">
      <div className="mb-5 flex flex-wrap gap-2">
        {config.platforms.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => setPlatform(p.value)}
            style={platform === p.value ? { backgroundColor: mission.color } : undefined}
            className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
              platform === p.value ? 'text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <FileDrop label={config.erpLabel} required {...fileProps} files={erp} onFiles={setErp} />
        <FileDrop
          label={`${platformLabel} ${config.platformFileLabel}`}
          required
          {...fileProps}
          files={platformFile}
          onFiles={setPlatformFile}
        />
      </div>
      {config.optionalFiles.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-3">
          {config.optionalFiles.map((f) => (
            <FileDrop
              key={f.field}
              label={`${f.label} (optional)`}
              note={f.note}
              {...fileProps}
              files={optional[f.field] ?? []}
              onFiles={(files) => setOptional((prev) => ({ ...prev, [f.field]: files }))}
            />
          ))}
        </div>
      )}

      <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-3">
        <Field label="Compare on">
          <select className={inputClass} value={matchOn} onChange={(e) => setMatchOn(e.target.value)}>
            <option value="payout">Payout vs Settlement</option>
            <option value="gross">Gross vs Gross</option>
          </select>
        </Field>
        <Field label="Tolerance (Rs.)">
          <input className={inputClass} type="number" step="0.5" value={tolerance} onChange={(e) => setTolerance(e.target.value)} />
        </Field>
        <Field label="Settlement cycle (days)">
          <input className={inputClass} type="number" step="1" value={cycleDays} onChange={(e) => setCycleDays(e.target.value)} />
        </Field>
        {config.stockHoldingDays && (
          <Field label="Stock holding limit (days)">
            <input className={inputClass} type="number" step="1" value={holdingDays} onChange={(e) => setHoldingDays(e.target.value)} />
          </Field>
        )}
        <Field label="As-of date (optional)">
          <input className={inputClass} type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        </Field>
        <label className="flex items-end gap-2 pb-2 text-sm text-foreground">
          <input type="checkbox" checked={stripZeros} onChange={(e) => setStripZeros(e.target.checked)} />
          Strip leading zeros in Order IDs
        </label>
      </div>

      <div className="mt-4 flex gap-3">
        <Button
          onClick={handleRun}
          disabled={status === 'loading'}
          style={{ backgroundColor: mission.color }}
          className="flex-1 text-white hover:opacity-90"
        >
          {status === 'loading' ? 'Reconciling…' : 'Run Reconciliation'}
        </Button>
        <Button variant="outline" onClick={handleDownload} disabled={!ready || downloading} className="flex-1">
          {downloading ? 'Preparing report…' : 'Download Excel Report'}
        </Button>
      </div>
      {status === 'loading' && <ProcessingNotice label="Reconciling…" />}
      {error && <ErrorNotice message={error} />}
      {status === 'idle' && !error && (
        <InfoNotice>📁 Upload the {config.erpLabel} and the {config.platformFileLabel} to begin.</InfoNotice>
      )}

      {status === 'success' && summary && (
        <div className="mt-4 space-y-4 text-sm">
          {summary.formats && (
            <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">
              ✅ Done. ERP format: {summary.formats.erp ?? '-'} · Platform format: {summary.formats.platform ?? '-'}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            {Object.entries(summary.headline).map(([key, value]) => (
              <Stat key={key} label={HEADLINE_LABELS[key] ?? key} value={value} tone={BAD_HEADLINES.has(key) && value > 0 ? 'bad' : undefined} />
            ))}
          </div>

          {summary.summary_rows.length > 0 && (
            <Section title="Full summary">
              <div className="max-h-[420px] overflow-auto rounded-md border border-border bg-background">
                <table className="w-full text-left text-xs">
                  <tbody>
                    {summary.summary_rows.map((r, i) => {
                      const metric = String(r.Metric ?? '');
                      if (metric === '' && (r.Value === '' || r.Value == null)) return <tr key={i}><td colSpan={2}>&nbsp;</td></tr>;
                      const heading = metric.startsWith('--') || metric.startsWith('RECONCILIATION SUMMARY');
                      return (
                        <tr key={i} className={`border-t border-border ${heading ? 'bg-muted font-semibold' : ''}`}>
                          <td className="px-3 py-1.5">{metric}</td>
                          <td className="px-3 py-1.5">{heading ? String(r.Value ?? '') : typeof r.Value === 'number' ? r.Value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(r.Value ?? '-')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {([
            ['Stock reconciliation (ERP vs platform stock)', summary.stock_reco],
            ['Unmatched orders (largest difference first)', summary.unmatched_preview],
            ['Cancelled / RTO / returned', summary.cancelled_preview],
            ['Exchanges / replacements', summary.exchanges],
            ['Matched orders', summary.matched_preview],
            ['Warehouse stock (sell-in vs sell-out)', summary.warehouse_stock],
            ['Stock aging (batch-wise)', summary.stock_aging],
          ] as const).map(([title, rows]) =>
            rows && rows.length > 0 ? (
              <Section key={title} title={`${title} (${rows.length})`}>
                <RecordTable rows={rows} />
              </Section>
            ) : null
          )}
          <p className="text-xs text-muted-foreground">Tables show a preview; the Excel report has every row.</p>
        </div>
      )}
    </MissionShell>
  );
}

export default MarketplaceReconPanel;
